import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Loader2, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CTAButton } from "@/components/CTAButton";
import { Reveal } from "@/components/motion/Reveal";
import { Seo } from "@/components/Seo";
import { postAudit, postBrochure } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

const POSTE_OPTIONS = [...Array.from({ length: 15 }, (_, i) => `${i + 1}`), "Plus de 15"];

export default function Contact() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [brochureEmail, setBrochureEmail] = useState("");
  const [brochureLoading, setBrochureLoading] = useState(false);
  const [form, setForm] = useState({ nom: "", entreprise: "", email: "", telephone: "", postes: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.postes) {
      toast.error("Merci de renseigner votre nom, votre email et le nombre de postes.");
      return;
    }
    setLoading(true);
    try {
      await postAudit(form);
      trackEvent("generate_lead", { method: "contact" });
      navigate("/merci");
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler au 03 20 49 02 04.");
    } finally {
      setLoading(false);
    }
  };

  const sendBrochure = async (e) => {
    e.preventDefault();
    if (!brochureEmail) { toast.error("Merci d'indiquer votre email."); return; }
    setBrochureLoading(true);
    try {
      await postBrochure(brochureEmail);
      trackEvent("generate_lead", { method: "plaquette" });
      toast.success("C'est envoyé ! Vérifiez votre boîte email, la plaquette arrive.");
      setBrochureEmail("");
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setBrochureLoading(false);
    }
  };

  return (
    <>
      <Seo title="Contact et échange gratuit à Lille" description="Contactez Umbo pour un échange gratuit de 30 minutes, sans engagement. Téléphone 03 20 49 02 04, contact@umbocyber.fr, à Lille (Hauts-de-France)." path="/contact" />
      <section className="bg-navy grain relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Contact</span></Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-white max-w-3xl text-balance">
              Parlons de votre entreprise.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl">
              Un échange sans engagement. Nous vous répondons rapidement pour convenir d'un premier échange de 30 minutes.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          {/* Coordonnées */}
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-display text-2xl font-semibold text-navy">Nos coordonnées</h2>
              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-navy grid place-items-center shrink-0"><Phone className="w-5 h-5 text-amber-500" /></div>
                  <div><p className="text-sm text-slate-500">Téléphone</p><a href="tel:+33320490204" className="text-navy font-medium link-underline">03 20 49 02 04</a></div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-navy grid place-items-center shrink-0"><Mail className="w-5 h-5 text-amber-500" /></div>
                  <div><p className="text-sm text-slate-500">Email</p><a href="mailto:contact@umbocyber.fr" className="text-navy font-medium link-underline">contact@umbocyber.fr</a></div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-navy grid place-items-center shrink-0"><MapPin className="w-5 h-5 text-amber-500" /></div>
                  <div><p className="text-sm text-slate-500">Adresse</p><p className="text-navy font-medium">679 avenue de la République,<br />59800 Lille</p></div>
                </li>
              </ul>

              <div className="mt-10 p-6 rounded-xl border border-slate-200 bg-[#f8fafc]">
                <div className="flex items-center gap-2 text-navy font-medium"><FileText className="w-4 h-4 text-amber-600" /> Recevoir la plaquette tarifaire</div>
                <p className="mt-2 text-sm text-slate-500">Nos deux formules et la grille complète, par email.</p>
                <form onSubmit={sendBrochure} className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="brochure-form">
                  <Input type="email" value={brochureEmail} onChange={(e) => setBrochureEmail(e.target.value)} placeholder="vous@entreprise.fr" data-testid="brochure-email" />
                  <CTAButton type="submit" variant="navy" disabled={brochureLoading} data-testid="brochure-submit">
                    {brochureLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Recevoir"}
                  </CTAButton>
                </form>
              </div>
            </Reveal>
          </div>

          {/* Formulaire */}
          <div className="lg:col-span-8">
            <Reveal delay={0.08}>
              <form onSubmit={submit} className="border border-slate-200 rounded-2xl p-8 sm:p-10 space-y-5" data-testid="contact-form">
                <h2 className="font-display text-2xl font-semibold text-navy">Réserver un échange gratuit</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5"><Label htmlFor="c-nom">Nom *</Label><Input id="c-nom" value={form.nom} onChange={set("nom")} placeholder="Votre nom" data-testid="contact-nom" /></div>
                  <div className="space-y-1.5"><Label htmlFor="c-entreprise">Entreprise</Label><Input id="c-entreprise" value={form.entreprise} onChange={set("entreprise")} placeholder="Nom de l'entreprise" data-testid="contact-entreprise" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5"><Label htmlFor="c-email">Email *</Label><Input id="c-email" type="email" value={form.email} onChange={set("email")} placeholder="vous@entreprise.fr" data-testid="contact-email" /></div>
                  <div className="space-y-1.5"><Label htmlFor="c-tel">Téléphone</Label><Input id="c-tel" value={form.telephone} onChange={set("telephone")} placeholder="03 XX XX XX XX" data-testid="contact-telephone" /></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Nombre de postes *</Label>
                  <Select value={form.postes} onValueChange={set("postes")}>
                    <SelectTrigger data-testid="contact-postes"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                    <SelectContent className="max-h-64">{POSTE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label htmlFor="c-msg">Votre message</Label><Textarea id="c-msg" rows={4} value={form.message} onChange={set("message")} placeholder="Dites-nous en quelques mots où vous en êtes." data-testid="contact-message" /></div>
                <CTAButton type="submit" size="lg" className="w-full" disabled={loading} data-testid="contact-submit">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer ma demande"}
                </CTAButton>
                <p className="text-center text-sm text-slate-400">Échange sans engagement · Réponse rapide</p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
